/**
 * Schede (Lead/Clienti) con lo stesso numero di una chat WhatsApp, con fase e
 * assegnatario per scegliere quale collegare.
 *
 * La ricerca e' quella della nuova chat (searchClientsForChat): rispetta il filtro
 * "solo i miei" e la visibilita' per pipeline/fonte del ruolo. Qui si aggiungono solo
 * fase e assegnatario delle schede trovate, in due letture.
 */
import { supabase } from '../../supabase';
import { getCachedLeadStates } from '../../stores/referenceDataCache';
import { searchClientsForChat, type ClientChatCandidate } from './clientPhoneSearch';
import { chatNumberDigits, filterByChatNumber, searchTermForDigits, type ChatNumberCandidate } from './chatNumberMatch';

const MAX_CANDIDATES = 20;

/** Aggiunge fase e assegnatario alle schede trovate dalla ricerca. */
export async function withStageAndAssignee(
  organizationId: string,
  rows: ClientChatCandidate[],
): Promise<ChatNumberCandidate[]> {
  if (rows.length === 0) return [];
  const { data: details } = await supabase
    .from('clients')
    .select('id, pipeline_id, lead_stage, assigned_to, sales_rep_id, pipeline_stage:lead_pipeline_stages!clients_pipeline_stage_id_fkey(name, color)')
    .eq('organization_id', organizationId)
    .in('id', rows.map((r) => r.id));
  const byId = new Map<string, any>((details || []).map((d: any) => [d.id, d]));

  const userIds = [...new Set((details || []).map((d: any) => d.assigned_to || d.sales_rep_id).filter(Boolean))] as string[];
  const needsLeadStates = (details || []).some((d: any) => !d.pipeline_id && d.lead_stage);
  const [profiles, leadStates] = await Promise.all([
    userIds.length > 0
      ? supabase.from('user_profiles').select('id, full_name, email').in('id', userIds).then((r) => r.data || [])
      : Promise.resolve([] as any[]),
    needsLeadStates ? getCachedLeadStates(organizationId).catch(() => []) : Promise.resolve([] as any[]),
  ]);
  const nameOf = new Map<string, string>((profiles as any[]).map((p) => [p.id, p.full_name || p.email || '']));

  return rows.map((r) => {
    const d = byId.get(r.id);
    const stage = d?.pipeline_id ? d?.pipeline_stage : (leadStates as any[]).find((s) => s.id === d?.lead_stage);
    const userId = d?.assigned_to || d?.sales_rep_id || null;
    return {
      id: r.id,
      name: r.name,
      kind: r.kind,
      numbers: r.numbers,
      stageName: stage?.name || null,
      stageColor: stage?.color || null,
      assigneeName: (userId && nameOf.get(userId)) || null,
    };
  });
}

/** Tutte le schede visibili all'utente che hanno il numero della chat (vuoto per gruppi e @lid). */
export async function findCandidatesForChat(
  organizationId: string,
  userId: string | null,
  chatJid: string,
): Promise<ChatNumberCandidate[]> {
  const digits = chatNumberDigits(chatJid);
  if (!organizationId || !digits) return [];
  const found = await searchClientsForChat(organizationId, userId, searchTermForDigits(digits), MAX_CANDIDATES);
  return withStageAndAssignee(organizationId, filterByChatNumber(digits, found));
}
